/**
 * TFMC Mobile Product Gallery v2.1
 * Native scroll-based gallery with free-flowing momentum.
 * No snapping — continuous scrolling like seed.com.
 * Thumbnail strip stays synced via IntersectionObserver.
 * Tap any image to view fullscreen lightbox.
 */
(function () {
  'use strict';

  class MobileGallery extends HTMLElement {
    constructor() {
      super();
      this._currentIndex = 0;
      this._thumbTapping = false;
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchStartTime = 0;
    }

    connectedCallback() {
      this._viewport = this.querySelector('.mg-viewport');
      this._track = this.querySelector('.mg-track');
      this._slides = this.querySelectorAll('.mg-slide');
      this._thumbContainer = this.querySelector('.mg-thumb-container');
      this._thumbStrip = this.querySelector('.mg-thumb-strip');
      this._thumbs = this.querySelectorAll('.mg-thumb');
      this._counter = this.querySelector('.mg-counter');
      this._slideCount = this._slides.length;

      if (this._slideCount < 1) return;

      this._setupThumbs();
      this._setupVariantListener();
      this._setupIntersectionObserver();
      this._setupLightbox();
      this._setupSlideTaps();
      this._isolateFromThemeSwipe();
    }

    disconnectedCallback() {
      this._io?.disconnect();
    }

    /* ── IntersectionObserver for slide tracking ── */

    _setupIntersectionObserver() {
      const options = {
        root: this._viewport,
        threshold: [0, 0.25, 0.5, 0.75, 1]
      };

      this._slideVisibility = new Map();

      this._io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this._slideVisibility.set(entry.target, entry.intersectionRatio);
        });

        let maxRatio = 0;
        let mostVisible = null;

        this._slideVisibility.forEach((ratio, slide) => {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            mostVisible = slide;
          }
        });

        if (mostVisible) {
          const index = parseInt(mostVisible.dataset.index, 10);
          if (index !== this._currentIndex) {
            this._currentIndex = index;
            this._updateActiveStates(index);
          }
        }
      }, options);

      this._slides.forEach((slide) => this._io.observe(slide));
    }

    /* ── Thumbnail taps ── */

    _setupThumbs() {
      this._thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this._thumbTapping = true;
          this._scrollToSlide(i, true);
          setTimeout(() => { this._thumbTapping = false; }, 500);
        });
      });
    }

    /* ── Variant change listener ── */

    _setupVariantListener() {
      const productId = this.dataset.productId;
      if (!productId) return;

      window.addEventListener(`theme:product:${productId}:variantChanged`, (e) => {
        const variant = e.detail?.variant;
        if (variant?.featured_media) {
          const mediaId = String(variant.featured_media.id);
          const idx = [...this._slides].findIndex(
            (s) => s.dataset.mediaId === mediaId
          );
          if (idx >= 0) {
            this._scrollToSlide(idx, true);
          }
        }
      });
    }

    /* ── Lightbox ── */

    _setupLightbox() {
      // Create lightbox DOM
      this._lightbox = document.createElement('div');
      this._lightbox.className = 'mg-lightbox';
      this._lightbox.innerHTML = `
        <button type="button" class="mg-lightbox-close" aria-label="Close">
          <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <img class="mg-lightbox-img" src="" alt="" />
      `;
      document.body.appendChild(this._lightbox);

      this._lightboxImg = this._lightbox.querySelector('.mg-lightbox-img');
      const closeBtn = this._lightbox.querySelector('.mg-lightbox-close');

      // Close on X tap
      closeBtn.addEventListener('click', () => this._closeLightbox());

      // Close on background tap (not on the image itself)
      this._lightbox.addEventListener('click', (e) => {
        if (e.target === this._lightbox) {
          this._closeLightbox();
        }
      });
    }

    _openLightbox(slide) {
      // Find the image in this slide
      const img = slide.querySelector('img');
      if (!img) return; // Only open for image slides

      // Use the highest-res srcset URL or fallback to src
      const srcset = img.getAttribute('srcset');
      let bestSrc = img.src;

      if (srcset) {
        // Parse srcset and pick the largest width
        const candidates = srcset.split(',').map((s) => {
          const parts = s.trim().split(/\s+/);
          const url = parts[0];
          const w = parseInt(parts[1], 10) || 0;
          return { url, w };
        });
        candidates.sort((a, b) => b.w - a.w);
        if (candidates.length > 0) {
          bestSrc = candidates[0].url;
        }
      }

      this._lightboxImg.src = bestSrc;
      this._lightboxImg.alt = img.alt || '';
      this._lightbox.classList.add('mg-lightbox-open');
      document.body.style.overflow = 'hidden';
    }

    _closeLightbox() {
      this._lightbox.classList.remove('mg-lightbox-open');
      document.body.style.overflow = '';
    }

    /* ── Slide tap detection ── */
    /* We need to distinguish a tap (open lightbox) from a swipe (scroll).
       Track touch start/end — if finger moved < 10px and held < 300ms, it's a tap. */

    _setupSlideTaps() {
      this._viewport.addEventListener('touchstart', (e) => {
        const touch = e.touches[0];
        this._touchStartX = touch.clientX;
        this._touchStartY = touch.clientY;
        this._touchStartTime = Date.now();
      }, { passive: true });

      this._viewport.addEventListener('touchend', (e) => {
        const touch = e.changedTouches[0];
        const dx = Math.abs(touch.clientX - this._touchStartX);
        const dy = Math.abs(touch.clientY - this._touchStartY);
        const dt = Date.now() - this._touchStartTime;

        // Only count as a tap if barely moved and quick
        if (dx < 10 && dy < 10 && dt < 300) {
          const slide = touch.target.closest('.mg-slide');
          // Skip video/model slides — let native controls handle those
          if (slide && !slide.querySelector('video, iframe, model-viewer')) {
            this._openLightbox(slide);
          }
        }
      }, { passive: true });
    }

    /* ── Isolate from theme SwipeListener ── */
    /* The Maker theme binds SwipeListener to `document` which intercepts
       horizontal swipes and calls preventDefault — killing our native
       scroll momentum. We stop propagation so it never sees our touches. */

    _isolateFromThemeSwipe() {
      this._viewport.addEventListener('touchmove', (e) => {
        e.stopPropagation();
      }, { passive: true });
    }

    /* ── Scroll helpers ── */

    _scrollToSlide(index, smooth) {
      const slide = this._slides[index];
      if (!slide) return;

      this._viewport.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }

    _updateActiveStates(index) {
      if (this._counter) {
        this._counter.textContent = `${index + 1} / ${this._slideCount}`;
      }

      this._thumbs.forEach((t, i) => {
        t.classList.toggle('mg-thumb-active', i === index);
      });

      if (!this._thumbTapping) {
        this._scrollThumbIntoView(index);
      }
    }

    _scrollThumbIntoView(index) {
      const thumb = this._thumbs[index];
      if (!thumb || !this._thumbContainer) return;

      const containerWidth = this._thumbContainer.offsetWidth;
      const thumbLeft = thumb.offsetLeft;
      const thumbWidth = thumb.offsetWidth;
      const targetScroll = thumbLeft - containerWidth / 2 + thumbWidth / 2;

      this._thumbContainer.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  }

  if (!customElements.get('mobile-gallery')) {
    customElements.define('mobile-gallery', MobileGallery);
  }
})();
